import CancelButton from 'components/buttons/CancelButton';
import PermissionButton from 'components/buttons/PermissionButton';
import SaveButton from 'components/buttons/SaveButton';
import Loader from 'components/Loader';
import { SIMPLE_TABLE_CELL_CLASS, SIMPLE_TABLE_HEAD_CLASS, SIMPLE_TABLE_ROW_CLASS } from 'components/tables/BasicTable';
import { Button } from 'components/ui/button';
import { Checkbox } from 'components/ui/checkbox';
import { Popover, PopoverContent, PopoverDescription, PopoverTitle, PopoverTrigger } from 'components/ui/popover';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from 'components/ui/select';
import { Switch } from 'components/ui/switch';
import { TableBody, TableCell, TableHead, TableHeader, TableRow } from 'components/ui/table';
import { useCallback, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import {
    useGetRelatedPartyRoleSettingsQuery,
    useUpdateRelatedPartyRoleSettingsMutation,
} from 'store/api/related-party-role-settings';
import { RelatedPartyRoleDto } from 'types/compliance';
import { cn } from 'utils/cn';
import { t } from 'utils/getTranslations';
import { CONFIGURATION_CARD_CLASS } from './configurationCard';
import {
    isAskedInAnyTier,
    lacksDetails,
    retiredTierChoicesOf,
    retiredTierCodesHeldBy,
    TierChoice,
    tierChoiceOf,
    tierChoicesOf,
    toUpdate,
    withItemKey,
    withTierChoice,
} from './relatedPartyRoleChoices';

const HEAD_CELL_CLASS = cn(SIMPLE_TABLE_HEAD_CLASS, 'p-2 font-semibold whitespace-nowrap');
const BODY_CELL_CLASS = cn(SIMPLE_TABLE_CELL_CLASS, 'p-2');

const CHOICE_LABEL_IDS: Record<TierChoice, string> = {
    NOT_ASKED: 'configuration.related-party-roles.not-asked',
    ASKED: 'configuration.related-party-roles.asked',
    MANDATORY: 'configuration.related-party-roles.mandatory',
};

const itemLabel = (itemKey: string) => t(`configuration.related-party-roles.items.${itemKey}`);

/** A tier column: a live tier, or one the dictionary retired that a saved role still holds. */
interface TierColumn {
    code: string;
    name: string;
    isRetired: boolean;
}

/**
 * The firm's related-party roles, one row per role in the order the API lists them: whether the firm uses the
 * role, how each client identification tier asks it, and what is asked of a person in it. Which controls a row
 * gets follows the role's kind, so a role added to the dictionary appears here without a change.
 */
const RelatedPartyRoleSettings = () => {
    const { data: settings, isLoading } = useGetRelatedPartyRoleSettingsQuery();
    const [updateSettings, { isLoading: isSaving }] = useUpdateRelatedPartyRoleSettingsMutation();
    const [draft, setDraft] = useState<RelatedPartyRoleDto[] | null>(null);

    const isEditMode = draft !== null;
    const savedRoles = settings?.roles ?? [];
    const roles = draft ?? savedRoles;
    const tiers = settings?.tiers ?? [];
    const itemOptions = settings?.itemOptions ?? [];
    const hasRoleWithoutDetails = roles.some(lacksDetails);
    // Retired tiers come from the saved roles, not the draft, so a column stays put while it is being dropped.
    const columns: TierColumn[] = [
        ...tiers.map(({ code, name }) => ({ code, name, isRetired: false })),
        ...retiredTierCodesHeldBy(
            savedRoles,
            tiers.map(({ code }) => code)
        ).map((code) => ({
            code,
            name: t('configuration.related-party-roles.retired-tier', { tier: code }),
            isRetired: true,
        })),
    ];

    const updateRole = useCallback((code: string, change: (role: RelatedPartyRoleDto) => RelatedPartyRoleDto) => {
        setDraft((current) => current?.map((role) => (role.code === code ? change(role) : role)) ?? null);
    }, []);

    const handleSave = useCallback(async () => {
        if (!draft) return;
        try {
            await updateSettings({ roles: draft.map(toUpdate) }).unwrap();
            setDraft(null);
        } catch {
            // The API layer shows the backend's message; the table stays in edit mode for a correction.
        }
    }, [draft, updateSettings]);

    if (isLoading) {
        return <Loader />;
    }

    return (
        <div className="flex flex-col gap-4">
            <div className="flex items-center justify-between">
                <h3 className="text-h3 text-balance">
                    <FormattedMessage id="configuration.related-party-roles.title" />
                </h3>
                {isEditMode ? (
                    <div className="flex gap-2">
                        <CancelButton onClick={() => setDraft(null)} disabled={isSaving} />
                        <SaveButton
                            onClick={() => {
                                void handleSave();
                            }}
                            disabled={isSaving || hasRoleWithoutDetails}
                            tooltip={
                                hasRoleWithoutDetails ? 'configuration.related-party-roles.details-missing' : undefined
                            }
                        />
                    </div>
                ) : (
                    <PermissionButton
                        permissions={['UPDATE.BACK_OFFICE_SETTINGS']}
                        variant="outlined"
                        size="small"
                        disabled={!settings}
                        onClick={() => setDraft(settings?.roles ?? null)}
                    >
                        <FormattedMessage id="edit" />
                    </PermissionButton>
                )}
            </div>
            <div className={CONFIGURATION_CARD_CLASS}>
                {/* Wide: the card scrolls sideways rather than squeezing a tier column. */}
                <div className="relative w-full overflow-x-auto">
                    <table className="w-full text-left text-sm">
                        <TableHeader className="[&_tr]:border-b-0">
                            <TableRow className={SIMPLE_TABLE_ROW_CLASS}>
                                <TableHead rowSpan={2} className={HEAD_CELL_CLASS}>
                                    <FormattedMessage id="configuration.related-party-roles.role" />
                                </TableHead>
                                <TableHead rowSpan={2} className={HEAD_CELL_CLASS}>
                                    <FormattedMessage id="configuration.related-party-roles.used" />
                                </TableHead>
                                <TableHead colSpan={columns.length + 1} className={cn(HEAD_CELL_CLASS, 'pb-0')}>
                                    <FormattedMessage id="configuration.related-party-roles.client-identification" />
                                </TableHead>
                            </TableRow>
                            <TableRow className={SIMPLE_TABLE_ROW_CLASS}>
                                {columns.map((column) => (
                                    <TableHead key={column.code} className={HEAD_CELL_CLASS}>
                                        {column.name}
                                    </TableHead>
                                ))}
                                {/* While editing it takes the width the other columns leave, which squeezes them to
                                    their content and keeps this one the same width whatever the details ticked: they
                                    wrap instead. */}
                                <TableHead className={cn(HEAD_CELL_CLASS, isEditMode && 'w-full')}>
                                    <FormattedMessage id="configuration.related-party-roles.client-identification-asks" />
                                </TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {roles.map((role) => (
                                <RoleRow
                                    key={role.code}
                                    role={role}
                                    savedRole={savedRoles.find(({ code }) => code === role.code) ?? role}
                                    columns={columns}
                                    itemOptions={itemOptions}
                                    isEditMode={isEditMode}
                                    isSaving={isSaving}
                                    onChange={updateRole}
                                />
                            ))}
                        </TableBody>
                    </table>
                </div>
            </div>
            <ul className="text-body2 text-fg-secondary flex flex-col gap-1">
                <li>
                    <FormattedMessage id="configuration.related-party-roles.hint-not-asked" />
                </li>
                <li>
                    <FormattedMessage id="configuration.related-party-roles.hint-asked" />
                </li>
                <li>
                    <FormattedMessage id="configuration.related-party-roles.hint-mandatory" />
                </li>
            </ul>
        </div>
    );
};

export default RelatedPartyRoleSettings;

// ─── Row ────────────────────────────────────────────────────────────────────

interface RoleRowProps {
    role: RelatedPartyRoleDto;
    savedRole: RelatedPartyRoleDto;
    columns: TierColumn[];
    itemOptions: string[];
    isEditMode: boolean;
    isSaving: boolean;
    onChange: (code: string, change: (role: RelatedPartyRoleDto) => RelatedPartyRoleDto) => void;
}

const RoleRow = ({ role, savedRole, columns, itemOptions, isEditMode, isSaving, onChange }: RoleRowProps) => {
    const tierOrder = columns.map(({ code }) => code);
    return (
        <TableRow className={cn(SIMPLE_TABLE_ROW_CLASS, !isEditMode && !role.isEnabled && 'opacity-50')}>
            <TableCell className={BODY_CELL_CLASS}>
                <p className="text-body1 font-semibold whitespace-nowrap">{role.name}</p>
            </TableCell>
            <TableCell className={BODY_CELL_CLASS}>
                <div className="flex items-center gap-2">
                    <Switch
                        checked={role.isLegallyRequired || role.isEnabled}
                        disabled={!isEditMode || isSaving || role.isLegallyRequired}
                        aria-label={`${role.name}: ${t('configuration.related-party-roles.used')}`}
                        onCheckedChange={(checked) =>
                            onChange(role.code, (current) => ({ ...current, isEnabled: checked }))
                        }
                    />
                    {role.isLegallyRequired && (
                        <span className="text-caption text-fg-secondary whitespace-nowrap">
                            <FormattedMessage id="configuration.related-party-roles.required-by-law" />
                        </span>
                    )}
                </div>
            </TableCell>
            {role.kind === 'ENTITY' ? (
                <TableCell colSpan={columns.length + 1} className={cn(BODY_CELL_CLASS, 'text-fg-secondary')}>
                    <FormattedMessage id="configuration.related-party-roles.never-on-client-identification" />
                </TableCell>
            ) : (
                <>
                    {columns.map((column) => (
                        <TableCell key={column.code} className={BODY_CELL_CLASS}>
                            <TierChoiceCell
                                role={role}
                                savedRole={savedRole}
                                column={column}
                                isEditMode={isEditMode}
                                isSaving={isSaving}
                                onChoose={(choice) =>
                                    onChange(role.code, (current) =>
                                        withTierChoice(current, column.code, choice, tierOrder)
                                    )
                                }
                            />
                        </TableCell>
                    ))}
                    <TableCell className={BODY_CELL_CLASS}>
                        <ItemsCell
                            role={role}
                            itemOptions={itemOptions}
                            isEditMode={isEditMode}
                            isSaving={isSaving}
                            onToggle={(itemKey, isChecked) =>
                                onChange(role.code, (current) => withItemKey(current, itemKey, isChecked, itemOptions))
                            }
                        />
                    </TableCell>
                </>
            )}
        </TableRow>
    );
};

// ─── Tier choice ────────────────────────────────────────────────────────────

interface TierChoiceCellProps {
    role: RelatedPartyRoleDto;
    savedRole: RelatedPartyRoleDto;
    column: TierColumn;
    isEditMode: boolean;
    isSaving: boolean;
    onChoose: (choice: TierChoice) => void;
}

const TierChoiceCell = ({ role, savedRole, column, isEditMode, isSaving, onChoose }: TierChoiceCellProps) => {
    const choice = tierChoiceOf(role, column.code);
    const choices = column.isRetired ? retiredTierChoicesOf(savedRole, column.code) : tierChoicesOf(role);
    if (!isEditMode || choices.length === 0) {
        return choice === 'NOT_ASKED' ? (
            <>
                <span aria-hidden="true">—</span>
                <span className="sr-only">{t(CHOICE_LABEL_IDS.NOT_ASKED)}</span>
            </>
        ) : (
            <span className="whitespace-nowrap">{t(CHOICE_LABEL_IDS[choice])}</span>
        );
    }
    return (
        <Select<TierChoice>
            value={choice}
            disabled={isSaving}
            onValueChange={(value) => {
                if (value !== null) onChoose(value);
            }}
        >
            <SelectTrigger size="sm" aria-label={`${role.name}: ${column.name}`} className="bg-paper min-w-[132px]">
                <SelectValue>{(value: TierChoice | null) => (value ? t(CHOICE_LABEL_IDS[value]) : '')}</SelectValue>
            </SelectTrigger>
            <SelectContent>
                {choices.map((option) => (
                    <SelectItem key={option} value={option}>
                        {t(CHOICE_LABEL_IDS[option])}
                    </SelectItem>
                ))}
            </SelectContent>
        </Select>
    );
};

// ─── What the client identification asks ────────────────────────────────────

interface ItemsCellProps {
    role: RelatedPartyRoleDto;
    itemOptions: string[];
    isEditMode: boolean;
    isSaving: boolean;
    onToggle: (itemKey: string, isChecked: boolean) => void;
}

const ItemsCell = ({ role, itemOptions, isEditMode, isSaving, onToggle }: ItemsCellProps) => {
    const intl = useIntl();
    const itemKeys = role.identificationItemKeys ?? [];
    const isAsked = isAskedInAnyTier(role);
    const isMissing = isEditMode && lacksDetails(role);

    return (
        <div className="flex min-w-[240px] items-center justify-between gap-2">
            <span className={cn(!isAsked && 'text-fg-secondary', isMissing && 'text-error')}>
                {itemKeys.length > 0
                    ? intl.formatList(itemKeys.map(itemLabel), { type: 'unit' })
                    : t('configuration.related-party-roles.no-details')}
            </span>
            {isEditMode && (
                <Popover>
                    <PopoverTrigger
                        disabled={!isAsked || isSaving}
                        render={
                            <Button
                                variant="ghost"
                                size="sm"
                                className="shrink-0"
                                aria-label={`${role.name}: ${t('configuration.related-party-roles.client-identification-asks')}`}
                            />
                        }
                    >
                        <FormattedMessage id="edit" />
                    </PopoverTrigger>
                    <PopoverContent align="end" positionerClassName="z-(--z-modal)" className="w-[320px]">
                        <PopoverTitle>
                            <FormattedMessage
                                id="configuration.related-party-roles.details-title"
                                values={{ role: role.name }}
                            />
                        </PopoverTitle>
                        <div className="flex flex-col gap-2">
                            {itemOptions.map((itemKey) => (
                                <label key={itemKey} className="text-body1 flex cursor-pointer items-center gap-2">
                                    <Checkbox
                                        checked={itemKeys.includes(itemKey)}
                                        disabled={isSaving}
                                        onCheckedChange={(checked) => onToggle(itemKey, checked)}
                                    />
                                    {itemLabel(itemKey)}
                                </label>
                            ))}
                        </div>
                        <PopoverDescription>
                            <FormattedMessage id="configuration.related-party-roles.company-hint" />
                        </PopoverDescription>
                    </PopoverContent>
                </Popover>
            )}
        </div>
    );
};
